import React, { createContext, useContext, useState, useCallback } from 'react'
import { brand as defaultBrand } from '../services/brand'
export interface BoardType {
  id: string
  name: string
  type: 'scrum' | 'kanban' | 'bug-tracker' | 'roadmap' | 'custom'
  enabled: boolean
  icon: string
  description: string
}
export interface BrandConfig {
  // Brand / White Label
  name: string
  tagline: string
  accentColor: string
  logoInitial: string
  company: string
  footerText: string
  jiraBaseUrl: string
  // Team
  teamName: string
  teamSize: string
  industry: string
  // Project
  projectKey: string
  methodology: 'scrum' | 'kanban' | 'safe' | 'custom'
  sprintDuration: number
  // Agent
  agentTone: string
  agentLanguage: string
  // Integrations
  jiraConnected: boolean
  confluenceConnected: boolean
  slackConnected: boolean
  githubConnected: boolean
  // Board Types
  boardTypes: BoardType[]
  // Setup
  setupCompleted: boolean
}
interface BrandContextType {
  brand: BrandConfig
  updateBrand: (updates: Partial<BrandConfig>) => void
  resetBrand: () => void
}
const defaultBoardTypes: BoardType[] = [
  {
    id: 'scrum',
    name: 'Scrum Board',
    type: 'scrum',
    enabled: true,
    icon: '🏃',
    description: 'Sprints, story points, burndown charts',
  },
  {
    id: 'kanban',
    name: 'Kanban Board',
    type: 'kanban',
    enabled: false,
    icon: '📋',
    description: 'Flujo continuo, WIP limits, lead time',
  },
  {
    id: 'bugs',
    name: 'Bug Tracker',
    type: 'bug-tracker',
    enabled: false,
    icon: '🐛',
    description: 'Seguimiento de bugs, severidad, resolución',
  },
  {
    id: 'roadmap',
    name: 'Roadmap',
    type: 'roadmap',
    enabled: false,
    icon: '🗺️',
    description: 'Planificación a largo plazo, epics, milestones',
  },
]
const defaultConfig: BrandConfig = {
  ...defaultBrand,
  jiraBaseUrl: 'https://acme-corp.atlassian.net/browse',
  teamName: 'Team Alpha',
  teamSize: '5-10',
  industry: 'Technology',
  projectKey: 'VSM',
  methodology: 'scrum',
  sprintDuration: 2,
  agentTone: 'Professional',
  agentLanguage: 'Spanish',
  jiraConnected: true,
  confluenceConnected: true,
  slackConnected: false,
  githubConnected: false,
  boardTypes: defaultBoardTypes,
  setupCompleted: false,
}
const BrandContext = createContext<BrandContextType>({
  brand: defaultConfig,
  updateBrand: () => {},
  resetBrand: () => {},
})
export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brandState, setBrandState] = useState<BrandConfig>(() => {
    try {
      const saved = localStorage.getItem('brand-config')
      if (saved)
        return {
          ...defaultConfig,
          ...JSON.parse(saved),
        }
    } catch {}
    return defaultConfig
  })
  const updateBrand = useCallback((updates: Partial<BrandConfig>) => {
    setBrandState((prev) => {
      const next = {
        ...prev,
        ...updates,
      }
      try {
        localStorage.setItem('brand-config', JSON.stringify(next))
      } catch {}
      return next
    })
  }, [])
  const resetBrand = useCallback(() => {
    setBrandState(defaultConfig)
    try {
      localStorage.removeItem('brand-config')
    } catch {}
  }, [])
  return React.createElement(
    BrandContext.Provider,
    {
      value: {
        brand: brandState,
        updateBrand,
        resetBrand,
      },
    },
    children,
  )
}
export function useBrandConfig() {
  return useContext(BrandContext)
}
